<template>
  <v-container>
    <v-row>
      <v-col cols="12" xs="12" md="8">
        <v-card outlined>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn v-if="showPositionCorrectionBtn"
              color="success"
              @click="updateAllPositions"
            >
              <v-icon class="mr-1">mdi-content-save</v-icon>
              {{ $lang("images.save_order") }}
            </v-btn>
            <v-btn v-if="localFiles.length && !autoUpload"
              color="success"
              :loading="uploadingAll > 0"
              @click="uploadAllImages"
            >
              <v-icon class="mr-1">mdi-cloud-upload</v-icon>
              {{ $lang("images.upload_all") }}
            </v-btn>
            <v-btn
              v-if="images.length"
              @click="removeAllImages"
              color="red"
              class="white--text"
            >
              <v-icon class="mr-1">mdi-delete</v-icon>
              {{ $lang("images.remove_all") }}
            </v-btn>
          </v-card-actions>
          <v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                <tr>
                  <th class="text-left" style="width: 10%">{{ $lang("images.table.headers.order") }}</th>
                  <th class="text-left">{{ $lang("images.table.headers.image") }}</th>
                  <th class="text-left"></th>
                  <th class="text-right">{{ $lang("images.table.headers.actions") }}</th>
                </tr>
                </thead>
                <draggable
                  tag="tbody"
                  @start="isDragging = true"
                  @end="isDragging = false" `
                  :options="dragOptions"
                  v-model="images"
                  @change="changePositions"
                >
                  <tr v-for="(image,i) in images" :key="`item-${i}`" class="drag-item">
                    <td class="text-left move-cursor">
                      <v-icon v-if="sortable">mdi-drag-vertical</v-icon>
                    </td>
                    <td class="text-left flex">
                      <v-img
                        width="100"
                        height="100"
                        :src="image.path"
                      ></v-img>
                    </td>
                    <td class="text-left flex">
                      <v-btn fab small @click="zoom(image)">
                        <v-icon>mdi-magnify-plus-outline</v-icon>
                      </v-btn>
                    </td>
                    <td class="text-right">
                      <v-btn fab small @click="removeImage(image)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                  <tr v-for="up in uploads" :key="`up-${up}`" class="col-12">
                    <td></td>
                    <td class="text-left">
                      <v-progress-circular
                        :size="30"
                        color="primary"
                        indeterminate
                      ></v-progress-circular>
                      {{ $lang('images.uploading') }}
                    </td>
                    <td class="text-right"></td>
                    <td class="text-right"></td>
                  </tr>
                  <tr v-if="(images.length === 0 && uploads === 0)" :key="`item-empty`">
                    <td colspan="4" class="subtitle text-center">{{ $lang("images.no_images") }}</td>
                  </tr>
                </draggable>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" xs="12" md="4">
        <image-uploader
          @multiple-files-added="multipleFilesAdded"
          :max-files="maxFiles"
          :preview="false"
        ></image-uploader>
      </v-col>
    </v-row>

    <v-dialog v-model="isOpenRemove" width="300">
      <v-card>
        <v-card-title>
          {{ $lang("main.crud.are_you_sure") }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="isOpenRemove = false">
            {{ $lang("main.no") }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="deletionLoading ? deletionLoading : deletionAllLoading"
            @click="remove()"
          >
            {{ $lang("main.yes") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="isOpenZoom" max-width="1200">
      <v-card>
        <v-img v-if="zoomFilePath" :src="zoomFilePath" width="100%"></v-img>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="closeZoom">
            {{ $lang("main.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
  import ImageUploader from "./ImageUploader";
  import images from "@/mixins/images";
  import draggable from 'vuedraggable';
  import { mapState } from "vuex";

  export default {
    name: "ImageManagement",
    components: {
      ImageUploader,
      draggable
    },
    props: {
      autoUpload: {
        type: Boolean,
        default: false
      },
      autoUpdatePositions: {
        type: Boolean,
        default: false
      },
      ownerId: {
        type: Number,
        required: true
      },
      owner: {
        type: String,
        required: true
      },
      sortable: {
        type: Boolean,
        default: false
      },
      maxFiles: {
        type: Number,
        default: 20
      }
    },
    mixins: [
      images
    ],
    data() {
      return {
        isOpenRemove: false,
        removeAll: false,
        changedPositions: false,
        isDragging: false,
        uploads: 0,
        uploadingAll: 0,
        activeImage: {},
        zoomFilePath: null,
        isOpenZoom: false
      }
    },
    created() {
      if (this.autoUpdatePositions && !this.sortable) {
        console.error('Automatic position updates and sorting may have the same meaning.')
      }
      this.getImages(this.owner, this.ownerId);
    },
    methods: {
      zoom(image) {
        if (!!image.sizes && !!image.sizes.high) {
          this.zoomFilePath = image.sizes.high;
        } else {
          this.zoomFilePath  = image.path
        }
        this.isOpenZoom = true;
      },
      closeZoom(){
        this.isOpenZoom = false;
        this.zoomFilePath = null;
      },
      multipleFilesAdded(files) {
        let localFiles = [...this.localFiles];
        let last = this.images.length;

        for (let k in files) {
          if (isFinite(k)) {
            last++;
            files[k]['index'] = last;
            if (this.autoUpload) {
              this.uploads++;
              this.uploadImage(this.owner, this.ownerId, files[k], (this.sortable ? last : null)).then(() => {
                this.uploads--;
              }).catch(() => {
                this.uploads--;
              })
            } else {
              files[k]['hash'] = Math.random().toString(12).replace('0.', '');
              localFiles.push(files[k]);
            }
          }
        }

        this.localFiles = localFiles;
      },
      changePositions({moved}) {
        if (!this.autoUpdatePositions) {
          return;
        }

        if (this.autoUpload || this.localFiles.length === 0) {
          return this.updateImage(this.owner, this.ownerId, moved.element.id, {position: (moved.newIndex + 1)})
        }

        this.uploadAllImages();
      },
      updateAllPositions() {
        const images = this.images;
        let listData = images.map((image, i) => {
          return image.id
        }).filter((e) => e != null);
        this.updateAllImages(this.owner, this.ownerId, {sorting: listData})
      },
      uploadAllImages() {
        this.uploadingAll = this.localFiles.length;
        for (let i = 0; i < this.localFiles.length; i++) {
          this.uploadImage(this.owner, this.ownerId, this.localFiles[i], this.localFiles[i].position).then(() => {
            this.$delete(this.localFiles, i);
            this.uploadingAll--;
          }).catch(() => {
            this.$delete(this.localFiles, i);
            this.uploadingAll--;
          })
        }
      },
      deleteLocalImage(value, attribute = 'hash') {
        const l = this.localFiles.length;
        for (let i = 0; i < l; i++) {
          if (this.localFiles[i][attribute] === value) {
            this.$delete(this.localFiles, i);
            return
          }
        }
      },
      remove() {
        if (this.removeAll) {
          this.removeAll = false;
          return this.deleteAllImages(this.owner, this.ownerId)
        }
        if (!!this.activeImage.id) {
          // remove remote file
          this.deleteImage(this.owner, this.ownerId, this.activeImage.id);
        } else {
          //remove locale file
          this.deleteLocalImage(this.activeImage.hash);
          this.isOpenRemove = false;
        }
      },
      removeImage(file) {
        this.isOpenRemove = true;
        this.activeImage = file;
      },
      removeAllImages() {
        this.isOpenRemove = true;
        this.removeAll = true;
      }
    },
    computed: {
      ...mapState({
        deletionLoading: state => state.images.deletionRequest.loading,
        deletionAllLoading: state => state.images.deletionAllRequest.loading
      }),
      dragOptions() {
        return {
          group: "images",
          disabled: !this.sortable,
          ghostClass: "ghost",
          forceFallback: false,
          fallbackOnBody: false,
          animation: 150,
          draggable: 'tr.drag-item'
        };
      },
      showPositionCorrectionBtn() {
        return (this.sortable && !this.autoUpdatePositions && this.localFiles.length === 0 && this.images.length > 0);
      }
    },
    watch: {
      uploadingAll(newVal, oldVal) {
        if (newVal === 0 && oldVal > 0) {
          this.localFiles = [];
          if (this.sortable) {
            setTimeout(() => this.updateAllPositions(), 100);
          }
        }
      },
      deletionLoading(newVal, oldVal) {
        if (!newVal && oldVal) {
          this.isOpenRemove = false;
          this.deleteLocalImage(this.activeImage.id, 'id')
        }
      },
      deletionAllLoading(newVal, oldVal) {
        if (!newVal && oldVal) {
          this.isOpenRemove = false
        }
      }
    }
  }
</script>

<style scoped>
  .move-cursor {
    cursor: move;
  }
</style>
